import { ref } from 'vue'
import { useOnlineStatus } from './useOnlineStatus'
import { useItemStore } from '@/stores/items'
import { addChange, getChanges, removeChange, saveImage } from '@/lib/offlineDb'
import type { CreateItemInput, UpdateItemInput } from '@collector/shared'
import type { LocalItemState } from '@/types/offline'

export function useOfflineQueue() {
  const { isOnline } = useOnlineStatus()
  const itemStore = useItemStore()
  const pendingCount = ref(0)

  async function refreshPendingCount() {
    const changes = await getChanges()
    pendingCount.value = changes.length
  }

  async function createItem(data: CreateItemInput, photo?: File): Promise<LocalItemState> {
    if (isOnline.value) {
      const item = await itemStore.createItem(data)
      return item
    }

    const tempId = crypto.randomUUID()
    await addChange({
      id: crypto.randomUUID(),
      type: 'create',
      tempId,
      data,
      timestamp: Date.now(),
      status: 'pending',
    })

    if (photo) {
      await saveImage(tempId, photo)
    }

    const now = new Date().toISOString()
    const optimisticItem: LocalItemState = {
      id: -Date.now(),
      userId: 1,
      name: data.name,
      description: data.description ?? null,
      category: data.category ?? 'other',
      isVirtual: data.isVirtual ?? false,
      artistOrManufacturer: data.artistOrManufacturer ?? null,
      origin: data.origin ?? null,
      location: data.location ?? null,
      notes: data.notes ?? null,
      dateAcquired: data.dateAcquired ?? null,
      acquiredFrom: data.acquiredFrom ?? null,
      museumOrGallery: data.museumOrGallery ?? null,
      city: data.city ?? null,
      tags: data.tags ?? [],
      imageThumbnail: photo ? URL.createObjectURL(photo) : null,
      imageLarge: null,
      createdAt: now,
      updatedAt: now,
      _pendingSync: true,
      _tempId: tempId,
    }

    itemStore.items.unshift(optimisticItem)
    await refreshPendingCount()
    return optimisticItem
  }

  async function updateItem(id: number, data: UpdateItemInput): Promise<LocalItemState> {
    if (isOnline.value) {
      const item = await itemStore.updateItem(id, data)
      return item
    }

    await addChange({
      id: crypto.randomUUID(),
      type: 'update',
      entityId: id,
      data,
      timestamp: Date.now(),
      status: 'pending',
    })

    const index = itemStore.items.findIndex((i) => i.id === id)
    if (index !== -1) {
      itemStore.items[index] = {
        ...itemStore.items[index],
        ...data,
        _pendingSync: true,
      } as LocalItemState
    }

    await refreshPendingCount()
    return itemStore.items[index] as LocalItemState
  }

  async function deleteItem(id: number): Promise<void> {
    if (isOnline.value) {
      await itemStore.deleteItem(id)
      return
    }

    await addChange({
      id: crypto.randomUUID(),
      type: 'delete',
      entityId: id,
      data: { itemId: id },
      timestamp: Date.now(),
      status: 'pending',
    })

    const index = itemStore.items.findIndex((i) => i.id === id)
    if (index !== -1) {
      itemStore.items.splice(index, 1)
    }

    await refreshPendingCount()
  }

  async function uploadImage(itemId: number, file: File): Promise<LocalItemState> {
    if (isOnline.value) {
      const item = await itemStore.uploadImage(itemId, file)
      return item
    }

    const tempId = crypto.randomUUID()
    await saveImage(tempId, file)
    await addChange({
      id: crypto.randomUUID(),
      type: 'upload',
      entityId: itemId,
      tempId,
      data: { itemId },
      timestamp: Date.now(),
      status: 'pending',
    })

    const localUrl = URL.createObjectURL(file)
    const index = itemStore.items.findIndex((i) => i.id === itemId)
    if (index !== -1) {
      itemStore.items[index] = {
        ...itemStore.items[index],
        imageThumbnail: localUrl,
        _pendingSync: true,
      } as LocalItemState
    }

    await refreshPendingCount()
    return itemStore.items[index] as LocalItemState
  }

  async function removeQueuedChange(changeId: string): Promise<void> {
    await removeChange(changeId)
    await refreshPendingCount()
  }

  return {
    pendingCount,
    refreshPendingCount,
    createItem,
    updateItem,
    deleteItem,
    uploadImage,
    removeQueuedChange,
  }
}
